import { BaseTableInputType } from "../../../../dtos/base-table-input-type";
import { BaseTableMetaData } from "../../../../dtos/base-table-meta-data";
import { DepositTypeEnum } from "../../../../dtos/deposit-type-enum";
import { MultiBaseTable } from "../../../../dtos/multi-base-table";
import { PaymentScheduleTableAndInvoicesDto } from "../../../../dtos/payment-schedule-table-and-invoices-dto";
import { PolicyBillingFrequencyEnum } from "../../../../dtos/policy-billing-frequency-enum";
import { PolicyBindInstructionsBlob } from "../../../../dtos/policy-bind-instructions-blob";
import { PolicyPayPlanBlob } from "../../../../dtos/policy-pay-plan-blob";
import { PolicyPaymentScheduleMultiTablePage } from "../../../../dtos/policy-payment-schedule-multi-table-page";
import { PolicyPaymentSchedulePage } from "../../../../dtos/policy-payment-schedule-page";
import { PolicyRatingBlob } from "../../../../dtos/policy-rating-blob";
import { PolicyStateBlob } from "../../../../dtos/policy-state-blob";
import { ProgramEndorsementBlob } from "../../../../dtos/program-endorsement-blob";
import { ProgramPayPlanDto } from "../../../../dtos/program-pay-plan-dto";
import { ProgramPolicyEndorsementBlob } from "../../../../dtos/program-policy-endorsement-blob";
import { SurchargesOrFeesEnum } from "../../../../dtos/surcharges-or-fees-enum";
import { conditionHasValue } from "../../../../utilities/conditionalSupportFunctions";
import {
  FormattingDate,
  getDateDiff,
  getDateObject,
  getDaysBetweenDates,
  getNumberOfDaysInAYear,
  getTimeBetweenDates,
  isDateAfterDate,
} from "../../../../utilities/dateFunctions";
import { getKeyFromEnumValue } from "../../../../utilities/enumFunctions";
import {
  customRound,
  unFormatLocalString,
} from "../../../../utilities/stringFunctions";
import { ColProps } from "../../../TrueUI/Grids/Col";
import {
  BaseTable2Properties,
  BaseTableChildrenData,
  ComputeForCellParameters,
} from "../../../TrueUI/Tables/BaseTable2/TableProperties";
import { GlobalInsuredAtomProperties } from "../../InsuredAtoms";
import { getRequiredAndOptionalFormsFromResponse } from "../PolicyEndorsementForms/EndorsementFormsUtils";
import { getTotalSurchargesAndFees } from "../PolicyQuoteExposurePremium/ExposurePremiumUtils";
import { getTotalPremium } from "../PolicyQuoteForm/PolicyQuoteUtils";
import {
  updatePolicyQuote,
  updatePolicyQuoteMultipleTargets,
} from "../updatesPolicyQuoteFunctions";
import {
  BindInstructionsInfoProps,
  ScheduleTableUIProps,
} from "./PolicyQuoteBindInstructionsTypes";

const AMOUNT = getKeyFromEnumValue(DepositTypeEnum, DepositTypeEnum.AMOUNT);
const INSTALL = getKeyFromEnumValue(DepositTypeEnum, DepositTypeEnum.INSTALL);
const PERCENT = getKeyFromEnumValue(DepositTypeEnum, DepositTypeEnum.PERCENT);
const REPORTING = getKeyFromEnumValue(
  DepositTypeEnum,
  DepositTypeEnum.REPORTING
);
const NON_EARNING = getKeyFromEnumValue(
  DepositTypeEnum,
  DepositTypeEnum.NON_EARNING
);
const FEE = getKeyFromEnumValue(SurchargesOrFeesEnum, SurchargesOrFeesEnum.FEE);
const TAX = getKeyFromEnumValue(SurchargesOrFeesEnum, SurchargesOrFeesEnum.TAX);
const SURCHARGE = getKeyFromEnumValue(
  SurchargesOrFeesEnum,
  SurchargesOrFeesEnum.SURCHARGE
);
export const InstallmentBillingMethod = "Installment";
export const depositTypes = {
  AMOUNT,
  INSTALL,
  PERCENT,
  REPORTING,
  NON_EARNING,
};

const surchargesOrFees = [FEE, TAX, SURCHARGE];

export const paymentPlanDepositTypeOptions = [
  {
    displayName: depositTypes.PERCENT,
    stringValue: depositTypes.PERCENT,
    intValue: null,
    decimalValue: null,
    dateValue: null,
  },
  {
    displayName: depositTypes.AMOUNT,
    stringValue: depositTypes.AMOUNT,
    intValue: null,
    decimalValue: null,
    dateValue: null,
  },
];

export const getStatesWithRatings = (states: PolicyStateBlob[]) =>
  states.map((state?: PolicyStateBlob) => ({
    stateCode: state?.stateCode,
    stateName: state?.stateName,
    ratings: getBindInstructionsStateRatingsByPolicyQuoteState(
      state?.ratings ?? []
    ),
  }));

const getBindInstructionsStateRatingsByPolicyQuoteState = (
  ratings: PolicyRatingBlob[]
) =>
  ratings.map((rating) => ({
    amount: rating.amount,
    proRate: rating.proRate,
    runningTotal: rating.runningTotal,
    elementCategory: rating.elementCategory,
  }));

const calculatedNonEarningBilledDepositAmount = (
  depositRate: number,
  billedPremiumAmount: number,
  numberPayments: number,
  bindDiscountAmount: number,
  depositType?: string
) => {
  const nonEarningAmount =
    depositType === depositTypes.PERCENT
      ? customRound(
          (
            billedPremiumAmount * (depositRate === 0 ? 1 : depositRate / 100)
          ).toString(),
          0
        )
      : depositRate;
  const installmentPayment = customRound(
    (billedPremiumAmount / numberPayments).toString(),
    0
  );
  const numberOfPayments = numberPayments - 1 === 0 ? 1 : numberPayments - 1;
  const totalAmount = installmentPayment * numberOfPayments;
  const billedDepositAmount = customRound(
    (billedPremiumAmount - totalAmount).toString(),
    0
  );

  return {
    nonEarningAmount,
    installmentPayment,
    billedDepositAmount,
    bindDiscountAmount,
  };
};

export const calculatePremiumProratedValues = (
  states?: PolicyStateBlob[] | null,
  effectiveDate?: Date | null,
  expirationDate?: Date | null
) => {
  const amount = getTotalPremium(states);
  const policyDaysYear = getNumberOfDaysInAYear(
    getDateObject(effectiveDate).getFullYear()
  );
  const policyDays = getDaysBetweenDates(
    getDateObject(effectiveDate),
    getDateObject(expirationDate)
  );
  const totalBilled = customRound(
    ((unFormatLocalString(amount) / policyDaysYear) * policyDays).toString(),
    0
  );
  return totalBilled;
};

export const calculateFeeProratedValues = (
  states?: PolicyStateBlob[] | null,
  effectiveDate?: Date | null,
  expirationDate?: Date | null
) => {
  const amountProrated = getSumOfTotalSurchargesAndFeesByProRate(
    states ?? [],
    true
  );
  const amountNoProrated = getSumOfTotalSurchargesAndFeesByProRate(
    states ?? [],
    false
  );
  const policyDaysYear = getNumberOfDaysInAYear(
    getDateObject(effectiveDate).getFullYear()
  );
  const policyDays = getDaysBetweenDates(
    getDateObject(effectiveDate),
    getDateObject(expirationDate)
  );
  const totalBilledFeeProrated = customRound(
    ((amountProrated / policyDaysYear) * policyDays).toString(),
    0
  );
  return totalBilledFeeProrated + amountNoProrated;
};

export const getSumOfTotalSurchargesAndFeesPerStateByProRate = (
  ratings: PolicyRatingBlob[],
  proRate: boolean
) => {
  return Object.values(ratings).reduce((prevValue, rating) => {
    const isSurchargesOrFees =
      (surchargesOrFees.includes(rating.elementCategory ?? "") &&
        rating.proRate === proRate) ??
      false;
    return prevValue + (isSurchargesOrFees ? rating.amount ?? 0 : 0);
  }, 0);
};

export const getSumOfTotalSurchargesAndFeesByProRate = (
  states: PolicyStateBlob[],
  proRate: boolean
) => {
  return states
    .map((state: PolicyStateBlob) => {
      return getSumOfTotalSurchargesAndFeesPerStateByProRate(
        state?.ratings ?? [],
        proRate
      );
    })
    .reduce(
      (a, value) =>
        (a ?? 0) + (value !== null && value !== undefined ? value : 0)
    ) as number;
};

export const calculateBilledPremiumByProrate = (
  prorate?: boolean,
  states?: PolicyStateBlob[] | null,
  effectiveDate?: Date | null,
  expirationDate?: Date | null
) =>
  prorate
    ? calculatePremiumProratedValues(states, effectiveDate, expirationDate)
    : unFormatLocalString(getTotalPremium(states ?? []));

export const calculateBilledFeesByProrate = (
  prorate?: boolean,
  states?: PolicyStateBlob[] | null,
  effectiveDate?: Date | null,
  expirationDate?: Date | null
) =>
  prorate
    ? customRound(
        calculateFeeProratedValues(
          states,
          effectiveDate,
          expirationDate
        ).toString(),
        0
      )
    : getTotalSurchargesAndFees(states ?? []);

export const getDepositTypeByOverride = (
  depositOverride?: boolean,
  depositTypeForDepositOverride?: string | null,
  depositType?: string | null
) =>
  depositOverride
    ? depositTypeForDepositOverride ?? depositTypes.PERCENT
    : depositType ?? "";

export const getPaymentPlanOptionAsPolicyPlan = (
  paymentPlanOptions?: ProgramPayPlanDto[] | null,
  paymentPlan?: number | null
): PolicyPayPlanBlob => {
  const paymentOption = paymentPlanOptions?.find(
    (payment) => payment.payPlanId === (paymentPlan ?? -1)
  );
  return convertFromProgramToPolicyPayPlan(paymentOption);
};

const convertFromProgramToPolicyPayPlan = (
  payPlan?: ProgramPayPlanDto
): PolicyPayPlanBlob => ({
  payPlanID: payPlan?.payPlanId,
  planName: payPlan?.planName,
  planType: payPlan?.planType,
  dueOnDay: payPlan?.dueOnDay,
  deposit: payPlan?.deposit?.toString(),
  depositAmount: payPlan?.depositAmount,
  depositRate: payPlan?.depositAmount,
  depositType: payPlan?.depositType,
  numberOfPayment: payPlan?.numberOfPayments,
  premiumTranAccountId: payPlan?.premiumTranAccountId,
  assessmentTranAccountID: payPlan?.assessmentTranAccountID,
  auditDueDays: payPlan?.auditDueDays,
  auditLateDays: payPlan?.auditLateDays,
  depositOverride: payPlan?.depositOverride,
  allEqual: payPlan?.allEqual,
  renewalFirstOffset: payPlan?.renewalFirstOffset,
  discountRate: payPlan?.discountRate,
  discountTranAccountId: payPlan?.discountTranAccountId,
  discountBillingDescription: payPlan?.discountBillingDescription,
});

export const calculatedBilledDepositAmount = (
  payPlan: PolicyPayPlanBlob | null | undefined,
  billedPremiumAmount: number,
  tmpDepositType?: string
) => {
  const depositRate = payPlan?.depositAmount ?? 0;
  const numberPayments = payPlan?.numberOfPayment ?? 0;
  const isNonEarning = payPlan?.depositType === depositTypes.NON_EARNING;
  const allEqual = payPlan?.allEqual ?? false;
  const depositType =
    tmpDepositType === depositTypes.NON_EARNING
      ? depositTypes.PERCENT
      : tmpDepositType;
  const discountRate = payPlan?.discountRate;
  const bindDiscountAmount =
    conditionHasValue(discountRate) && discountRate !== 0
      ? Math.ceil((billedPremiumAmount * discountRate) / 100)
      : 0;
  const billedPremiumMinusDiscount = billedPremiumAmount - bindDiscountAmount;

  if (payPlan?.planType === depositTypes.REPORTING) {
    const calculatedAmount =
      billedPremiumMinusDiscount *
      (payPlan?.depositAmount === 0 ? 1 : (payPlan?.depositAmount ?? 0) / 100);
    return {
      installmentPayment: 0,
      billedPremiumAmount: customRound(calculatedAmount.toString(), 0),
      nonEarningAmount: null,
      bindDiscountAmount,
    };
  }
  if (isNonEarning) {
    return calculatedNonEarningBilledDepositAmount(
      depositRate,
      billedPremiumMinusDiscount,
      numberPayments,
      bindDiscountAmount,
      depositType
    );
  }
  if (depositType === depositTypes.PERCENT && !allEqual) {
    const baseDepositAmount = customRound(
      (
        billedPremiumMinusDiscount * (depositRate === 0 ? 1 : depositRate / 100)
      ).toString(),
      0
    );
    const remainingPremium = billedPremiumMinusDiscount - baseDepositAmount;
    const numberOfPayments = numberPayments - 1 === 0 ? 1 : numberPayments - 1;
    const installmentPayment = customRound(
      (remainingPremium / numberOfPayments).toString(),
      0
    );
    const totalAmount = installmentPayment * numberOfPayments;
    const billedDepositAmount = customRound(
      (billedPremiumMinusDiscount - totalAmount).toString(),
      0
    );

    return {
      installmentPayment,
      billedDepositAmount,
      nonEarningAmount: null,
      bindDiscountAmount,
    };
  }
  if (depositType === depositTypes.PERCENT && allEqual) {
    const equalAmount = billedPremiumMinusDiscount / numberPayments;
    const equalAmountRounded = customRound(equalAmount.toString(), 0);

    return {
      installmentPayment: equalAmountRounded,
      billedDepositAmount: equalAmountRounded,
      nonEarningAmount: null,
      bindDiscountAmount,
    };
  }
  if (payPlan?.depositType === depositTypes.AMOUNT) {
    return {
      installmentPayment: 0,
      billedDepositAmount: payPlan?.depositAmount,
      nonEarningAmount: null,
      bindDiscountAmount,
    };
  }

  return {
    installmentPayment: 0,
    billedDepositAmount: depositRate,
    nonEarningAmount: null,
    bindDiscountAmount,
  };
};

export const isDepositOverride = (
  paymentPlan: number,
  paymentPlans: ProgramPayPlanDto[]
) =>
  getPaymentPlanOptionAsPolicyPlan(paymentPlans, paymentPlan)
    ?.depositOverride === 1;

export const getBillingMethod = (planType?: string | null) =>
  planType === depositTypes.INSTALL ? InstallmentBillingMethod : planType ?? "";

export const getTextAlign = (
  paymentPlan,
  paymentPlans
): "left" | "center" | "right" =>
  !isDepositOverride(paymentPlan ?? -1, paymentPlans ?? []) ? "right" : "left";

export const getLabelAlign = (
  paymentPlan,
  paymentPlans
): "start" | "center" | "end" =>
  !isDepositOverride(paymentPlan ?? -1, paymentPlans ?? []) ? "end" : "start";

export const getVariant = (
  paymentPlan,
  paymentPlans
): "filled" | "standard" | "outlined" =>
  !isDepositOverride(paymentPlan ?? -1, paymentPlans ?? [])
    ? "standard"
    : "filled";

export const getCommonPropsForDepositRate = (
  paymentPlan,
  paymentPlans,
  readOnly
) => {
  return {
    align: getTextAlign(paymentPlan, paymentPlans),
    labelAlign: getLabelAlign(paymentPlan, paymentPlans),
    variant: getVariant(paymentPlan, paymentPlans),
    readOnly:
      readOnly || !isDepositOverride(paymentPlan ?? -1, paymentPlans ?? []),
  };
};

export const getLongevityThreshold = (
  firstPolicyDate?: Date | null,
  effectiveDate?: Date | null
) =>
  firstPolicyDate ? getTimeBetweenDates(firstPolicyDate, effectiveDate) : 0;

export const getProgramPayPlanOptions = (paymentPlans: ProgramPayPlanDto[]) =>
  paymentPlans.map((opt) => ({
    displayName: opt.planName ?? "",
    intValue: opt.payPlanId,
    stringValue: null,
    decimalValue: null,
    dateValue: null,
  }));

export const colWithHorizontalGutterOnly: ColProps = {
  horizontalMargin: "0px",
  verticalMargin: "0px",
  verticalGutter: "0px",
  horizontalGutter: "15px",
  horizontalAlign: "flex-start",
};

export const disabledProrateTerm = (
  effectiveDate?: Date | null,
  expirationDate?: Date | null
) =>
  getDateDiff(
    getDateObject(FormattingDate(effectiveDate)),
    getDateObject(FormattingDate(expirationDate)),
    "years",
    true
  ) === 1;

export const getTableInfoByBillByLocation = (
  billByLocation?: boolean,
  responseData?: PaymentScheduleTableAndInvoicesDto
) => {
  if (billByLocation === true) {
    const responseMultiScheduleData =
      responseData?.scheduleMultiTable as PolicyPaymentScheduleMultiTablePage;
    return {
      scheduleMultiTable: responseMultiScheduleData?.tableData,
      multiTableRowChildren: responseMultiScheduleData?.rowChildren,
    };
  }
  const responseScheduleData =
    responseData?.scheduleTable as PolicyPaymentSchedulePage;
  return {
    scheduleTable: responseScheduleData?.tableData,
  };
};

export const getChildrenData = (
  scheduleTableInfo?: ScheduleTableUIProps | null
): BaseTableChildrenData[] =>
  scheduleTableInfo?.multiTableRowChildren?.map(
    (children) =>
      ({
        groupId: children.id,
        columns: scheduleTableInfo?.scheduleMultiTable?.columns,
        data: children.rowData.map((row) => [
          "",
          row[0],
          row[1],
          row[2],
          row[3],
        ]),
      } as BaseTableChildrenData)
  ) ?? [];

export const validateInvoicesDueDateOnChange = (
  expirationDate?: Date | null,
  options?: any,
  setIsDialogOpen?: (isOpen: boolean) => void
) => {
  if (
    conditionHasValue(options) &&
    conditionHasValue(expirationDate) &&
    isDateAfterDate(options?.currentValue, expirationDate)
  )
    setIsDialogOpen?.(true);

  return options.currentValue;
};

export const getCommonTablesProps = (
  readOnly,
  updateDueDateForInvoiceByRegularTable?: (
    options?: ComputeForCellParameters<any>
  ) => void
): Partial<BaseTable2Properties> => ({
  columnOptions: [
    {
      fieldName: "UniqueKey",
      computeOnChange: {
        conditionForCell: [],
      },
    },
    {
      fieldName: "DueDate",
      width: 15,
      computeOnChange: {
        conditionForCell: [
          (options) => updateDueDateForInvoiceByRegularTable?.(options),
        ],
      },
    },
    {
      fieldName: "Description",
      width: 25,
    },
    {
      fieldName: "NonEarning",
      align: "right",
    },
    {
      fieldName: "Fees",
      align: "right",
    },
    {
      fieldName: "Premium",
      align: "right",
    },
    {
      fieldName: "Total",
      align: "right",
    },
  ],
  toolbarOptions: {
    showSaveButton: false,
    showAddButton: false,
    showEditButton: false,
    showSortFilter: false,
    showImportButton: false,
    showExcelButton: readOnly,
    showPDFButton: readOnly,
  },
});

export const getDueDateByHeaderParam = (headerParams?: BaseTableMetaData[]) => {
  const param = headerParams?.find(
    (p) => p.inputType === BaseTableInputType.DATE_PICKER
  );
  return param?.value ?? null;
};

export const getMultiTableGroups = (
  groupRowIndex: string,
  newDueDate: Date,
  scheduleMultiTable?: MultiBaseTable | null
) =>
  scheduleMultiTable?.groups.map((group) => {
    return group?.groupUiidKey === groupRowIndex
      ? {
          ...group,
          header: group.header.map((header) => {
            return header.inputType === BaseTableInputType.DATE_PICKER
              ? { ...header, value: FormattingDate(newDueDate) }
              : header;
          }),
        }
      : group;
  });

export const getAtomUpdatedByEndorsementForms = (
  atomValue: GlobalInsuredAtomProperties | null,
  allForms: ProgramEndorsementBlob[]
) => {
  const { requiredForms, optionalForms } =
    getRequiredAndOptionalFormsFromResponse(
      allForms,
      atomValue?.policyQuoteInformation?.policyQuote
    );
  const requiredFormValues = Object.values(
    requiredForms
  ) as ProgramPolicyEndorsementBlob[][];
  const optionalFormValues = Object.values(
    optionalForms
  ) as ProgramPolicyEndorsementBlob[][];

  const requiredFormsFlat = requiredFormValues.flatMap((form) => form);
  const optionalFormsFlat = optionalFormValues.flatMap((form) => form);

  const newAtomValue = updatePolicyQuote(
    atomValue,
    "endorsementForms",
    requiredFormsFlat.concat(optionalFormsFlat)
  );

  return newAtomValue;
};

const getBillingFrequencyByNumberOfPayments = (numberOfPayments: number) => {
  switch (numberOfPayments) {
    case 1:
      return PolicyBillingFrequencyEnum.ANNUAL;
    case 4:
      return PolicyBillingFrequencyEnum.QUARTERLY;
    case 2:
      return PolicyBillingFrequencyEnum.SEMI_ANNUAL;
    default:
      return PolicyBillingFrequencyEnum.MONTHLY;
  }
};

export const getNewAtomValueByBindInstructions = (
  atomValue: GlobalInsuredAtomProperties | null,
  bindInstructionUI?: BindInstructionsInfoProps | null
) => {
  const billingFrequency =
    atomValue?.policyQuoteInformation?.policyQuote?.configuration?.billingFrequencyList.find(
      (billingFrequency) =>
        billingFrequency.value ===
        getBillingFrequencyByNumberOfPayments(
          bindInstructionUI?.payPlan?.numberOfPayment ?? 0
        )
    );

  const bindInstructions = {
    ...atomValue?.policyQuoteInformation?.policyQuote?.bindInstructions,
    billedPremium: bindInstructionUI?.billedPremium ?? null,
    billedFees: bindInstructionUI?.billedFees ?? null,
    nonEarningAmount: bindInstructionUI?.nonEarningAmount ?? null,
    billedDepositAmount: bindInstructionUI?.billedDepositAmount ?? null,
    installmentPaymentAmount: bindInstructionUI?.installmentPayment ?? null,
    bindDiscountAmount: bindInstructionUI?.bindDiscountAmount ?? null,
    billingMethod: bindInstructionUI?.billingMethod ?? null,
    billByLocation: bindInstructionUI?.billByLocation ?? null,
    depositTypeForDepositOverride:
      bindInstructionUI?.depositTypeForDepositOverride ?? null,
    depositRate: bindInstructionUI?.depositRate ?? 1,
    prorateToTerm: bindInstructionUI?.prorateToTerm ?? false,
  } as PolicyBindInstructionsBlob;

  const newAtomValue = updatePolicyQuoteMultipleTargets(
    atomValue,
    ["billingFrequency", "payPlan", "bindInstructions", "invoices"],
    [
      billingFrequency,
      bindInstructionUI?.payPlan,
      bindInstructions,
      bindInstructionUI?.invoices,
    ]
  );

  return newAtomValue;
};

export const getCurrentTotalPremium = (
  bindInstructionsInfo: BindInstructionsInfoProps | null
) => {
  const billedDepositAmount = bindInstructionsInfo?.billedDepositAmount ?? 0;
  const installmentPayment = bindInstructionsInfo?.installmentPayment ?? 0;
  const numberOfPayment = bindInstructionsInfo?.payPlan?.numberOfPayment ?? 0;
  const bindDiscountAmount = bindInstructionsInfo?.bindDiscountAmount ?? 0;

  return (
    billedDepositAmount +
    installmentPayment * (numberOfPayment - 1) +
    bindDiscountAmount
  );
};
